import { AKMeta, ChatMessageUpdate, ErrorItem } from "@aidkitorg/types/lib/convoai/conversation";
import { ConversationItemContent, RealtimeClientEvent, ResponseCreateEvent, RealtimeServerEvent, ResponseAudioTranscriptDoneEvent, ResponseTextDoneEvent, ConversationItemWithReference } from 'openai/resources/beta/realtime/realtime';
import { useCallback, useMemo, useRef, useContext, useEffect, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { OpenAIWebRTCContext } from "../WebRTCContext";
import slugid from 'slugid';
import { v4 } from 'uuid';
import zod from 'zod';
import { zodResponseFormat } from "openai/helpers/zod";
import InterfaceContext from "../../Context";
import { ChatSessionContext } from "../ChatSessionContext";
import { ApplicationStateContext } from "../ApplicationStateContext";
import { ChatConversationContext, ControlSignal } from "./ConversationContext";
import { ChatContextType, Tool, useTools } from "./ChatContributor";

export type AudioChatEvent = RealtimeServerEvent | RealtimeClientEvent | ControlSignal;

export function useOpenAIVoiceChat(): ChatContextType {
  const { lang } = useContext(InterfaceContext);
  const webrtc = OpenAIWebRTCContext();
  const tools = useRef<Record<string, Tool<zod.ZodObject<any>, any>>>({});
  const [responding, setResponding] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const chatSession = useContext(ChatSessionContext);
  const updateConversation = usePost('/coach/conversation/update');
  const knownItems = useRef<Set<string>>(new Set());
  const {
    dispatch,
    phase,
    ...state
  } = useContext(ChatConversationContext);

  const nextResponse = useRef<ConversationItemWithReference[]>([]);

  useEffect(() => {
    dispatch({ type: 'update_phase', phase: !webrtc.active ? 'initializing' : webrtc.phase })
  }, [webrtc.phase, webrtc.active]);

  useAsyncEffect(async () => {
    if (chatSession.loaded && !initialized) {
      setInitialized(true);
      state.messages.map(msg => msg.id!).forEach(id => knownItems.current.add(id));
      await webrtc.connect(state.messages);
    }
  }, [chatSession.loaded, initialized, state.messages]);

  useEffect(() => {
    return () => webrtc.disconnect();
  }, []);

  const { state: appState } = useContext(ApplicationStateContext);

  useEffect(() => {

    const contextItems: string[] = [];

    if (appState.currentSection) {
      contextItems.push(`Current Section: ${appState.currentSection.name}`);
    }

    if (appState.currentQuestion) {
      contextItems.push(`Current Question: ${appState.currentQuestion.content}`);
    }

    const appInfo = JSON.stringify(Object.fromEntries(
      Object.values(appState.questions)
        .map(q => [q.targetField, q.value])
    ));
    contextItems.push(`Applicant's Information: ${appInfo}`);

    const visibleQuestions = JSON.stringify(
      Object.values(appState.questions)
        .filter(q => q.visible)
        .map(q => q.content)
    );
    contextItems.push(`Visible Questions: ${visibleQuestions}`);


    const questionsWithProblems = JSON.stringify(Object.fromEntries(
      Object.values(appState.questions)
        .filter(q => q.validity?.state === 'invalid')
        .map(q => [q.content, q.validity?.message]))
    );

    contextItems.push(`Questions with Problems: ${questionsWithProblems}`);

    const conversation: ConversationItemWithReference[] = state.messages
      .filter(({ id }) => {
        if (!id) return false;
        else if (id.length > 32) {
          console.warn('ID is too long, omitting from conversation', id);
          return false;
        }
        return true;
      })
      .map(({ id }) => ({ id, type: 'item_reference' }));

    nextResponse.current = conversation.concat({
      type: 'message',
      role: 'system',
      content: [{
        type: 'input_text',
        text: contextItems.join('\n\n')
      }]
    });

  }, [appState.questions, appState.currentSection, state.messages]);

  const lastMessage = useMemo(() => state.messages[state.messages.length - 1], [state.messages]);

  //TODO(luke): Figure out if we can do this in the backend somehow.
  useAsyncEffect(async () => {
    if (state.latestUpdatedItem?.meta?.phase === 'done' && chatSession.conversationId) {
      await updateConversation({
        id: chatSession.conversationId,
        messages: [state.latestUpdatedItem]
      });
    }
  }, [state.latestUpdatedItem, chatSession.conversationId, initialized]);

  const eventCallback = useCallback((event: RealtimeServerEvent | RealtimeClientEvent) => {
    if (!initialized) return;

    switch (event.type) {
      case 'input_audio_buffer.speech_started':
        if (responding) {
          setResponding(false);
          webrtc.sendEvent({
            type: 'response.cancel'
          });
        }
        break;
      case 'input_audio_buffer.speech_stopped':
        if (!responding) {
          setResponding(true);
          webrtc.sendEvent({
            type: 'response.create',
            response: {
              input: nextResponse.current?.length ? [
                ...nextResponse.current,
                { type: 'item_reference', id: event.item_id }
              ] : undefined
            }
          });
        }
        break;
      case 'response.done':
        setResponding(false);
        break;
      default:
        break;
    }
    const item = AudioChatAdapter(event);
    if (item && 'id' in item) {
      knownItems.current.add(item.id!);
    }
    dispatch(item);
  }, [initialized, webrtc.sendEvent, setResponding]);

  useEffect(() => {
    const unsubToken = webrtc.subscribe(
      eventCallback,
      initial => initial.forEach(e => dispatch(AudioChatAdapter(e)))
    );
    return () => unsubToken();
  }, [eventCallback, webrtc.subscribe, dispatch]);

  const reset = useCallback(() => {
    webrtc.setEnabled(false);
    dispatch({ type: 'clear_chat' });
    setTimeout(() => webrtc.setEnabled(true), 100);
  }, []);

  // setting the audio transcription language improves accuracy and latency
  // but it will signal Realtime to only transcribe in that language.
  // this means that if a user speaks in a language different than what we have specified,
  // Realtime will fail to respond in their spoken language
  useEffect(() => {
    if (webrtc.active && webrtc.realtimeSession?.id) {
      webrtc.updateSession({
        input_audio_transcription: {
          ...webrtc.realtimeSession.input_audio_transcription,
          language: lang,
          prompt: webrtc.realtimeSession.input_audio_transcription?.prompt ?? ""
        }
      })
    }
  }, [lang, webrtc.realtimeSession?.id, webrtc.active]);

  const sendMessage = useCallback((message: ChatMessageUpdate, opts?: { changeMessageId?: string, createResponse?: boolean, modalities?: ['audio', 'text'] | ['text'] }) => {
    if (message.type === 'ignore') return;

    const id = opts?.changeMessageId ?? message.id?.substring(0, 32) ?? slugid.encode(v4());

    knownItems.current.add(id);

    switch (message.type) {
      case 'update_meta':
        return;
      case 'tool_call':
        // this is specifically to account 
        // for when we are backfilling
        // from a previous session.
        webrtc.sendEvent({
          type: "conversation.item.create",
          item: {
            id,
            type: "function_call",
            call_id: message.callId,
            name: message.name,
            arguments: message.arguments
          }
        });
        break;
      case 'tool_call_output':
        webrtc.sendEvent({
          type: "conversation.item.create",
          previous_item_id: message.previousItemId,
          item: {
            id,
            type: "function_call_output",
            call_id: message.callId,
            output: message.output
          }
        });
        break;
      case 'appState':
        break;
      case 'message': {
        const content: ConversationItemContent[] = []
        for (const c of message.content) {
          switch (c.type) {
            case 'text':
              content.push({ type: message.role === 'assistant' ? 'text' : 'input_text', text: c.content });
              break;
            default: break;
          }
        }

        // also send message to remote
        webrtc.sendEvent({
          type: "conversation.item.create",
          item: {
            id,
            type: "message",
            role: message.role,
            content
          }
        });

        break;
      }
      case 'error':
        return;
    }

    if (opts?.createResponse || message.meta?.createResponse) {
      const response: ResponseCreateEvent = {
        type: "response.create",
        response: {
          modalities: opts?.modalities,
          input: nextResponse.current?.length ? [
            ...nextResponse.current,
            { type: 'item_reference', id: message.id }
          ] : undefined
        }
      };

      if (responding) {
        webrtc.sendEvent({
          type: 'response.cancel'
        });
        setResponding(false);
      }
      webrtc.sendEvent(response);
    }
  }, [webrtc.sendEvent, responding]);

  useEffect(() => {
    if (lastMessage?.id && !knownItems.current.has(lastMessage.id)) {
      sendMessage(lastMessage, { modalities: ['audio', 'text'] });
    }
  }, [lastMessage, sendMessage]);

  const registerTool = useCallback(<T extends zod.ZodObject<any>, Out>(tool: Tool<T, Out>) => {
    const newTools = { ...tools.current, [tool.name]: tool as unknown as Tool<zod.ZodObject<any>, any> };

    tools.current = newTools;

    if (webrtc.active) {
      webrtc.sendEvent({
        type: 'session.update',
        session: {
          tools: Object.values(newTools).map(tool => {
            const { schema } = zodResponseFormat(tool.schema, 'schema').json_schema;
            return {
              type: 'function',
              parameters: schema,
              name: tool.name,
              description: tool.description
            }
          })
        }
      });
    }
  }, [webrtc.active, webrtc.sendEvent, tools]);

  useTools({
    sendMessage,
    tools: tools.current
  });

  return {
    ready: !responding && initialized,
    sendMessage,
    registerTool,
    tools: tools.current,
    enabled: webrtc.enabled,
    setEnabled: webrtc.setEnabled,
    active: webrtc.active
  }
}

function AudioChatAdapter(action: AudioChatEvent): ChatMessageUpdate {

  const noItemId = () => ({
    type: 'error',
    error: {
      type: 'adapter_internal_error',
      code: 'missing_item_id',
      message: `missing an item id for the ${action.type}`
    },
    sourceObj: action
  } as ErrorItem);

  switch (action.type) {

    case "conversation.item.create":
    case "conversation.item.created":
    case "response.output_item.added":

      if (!action.item.id) return noItemId();

      const meta: AKMeta = {
        version: 0,
      };

      switch (action.item.type) {
        case 'message':
          return {
            id: action.item.id,
            type: 'message',
            content: action.item?.content?.map(c => ({ type: 'text', content: c.text! })) ?? [],
            role: action.item.role || 'user',
            meta
          };
        case 'function_call':
          return {
            id: action.item.id,
            type: 'tool_call',
            arguments: action.item.arguments || '',
            callId: action.item.call_id || '[no call_id]',
            name: action.item.name || '[no function name]',
            meta
          }
        case 'function_call_output':
          return {
            id: action.item.id,
            type: 'tool_call_output',
            callId: action.item.call_id || '[no call_id]',
            output: action.item.output || '[no output]',
            meta
          }
        default:
          return {
            type: 'error',
            error: {
              type: 'unsupported_item_type',
              message: `unsupported type found: ${action.item.type}`
            },
            sourceObj: action
          };
      }

    case "input_audio_buffer.speech_started":
      return {
        type: 'ignore'
      };

    case "input_audio_buffer.speech_stopped":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          phase: "pending",
          version: 1
        }
      };

    // remote added a new response
    case "conversation.item.input_audio_transcription.delta":
    case "response.audio_transcript.delta":
    case "response.text.delta": {
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index ?? 0,
        content: { type: 'text', content: action.delta || '' },
        sourceObj: action
      }
    }
    case "response.output_item.done":
      if (!action.item.id) return noItemId();
      // remote has completed the response
      return {
        id: action.item.id,
        type: 'update_meta',
        meta: {
          phase: "done"
        }
      };
    case "conversation.item.input_audio_transcription.completed":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          phase: "done"
        }
      };
    case "response.text.done":
    case "response.audio_transcript.done": {
      let completedText = (action as ResponseTextDoneEvent).text;
      let completedTranscript = (action as ResponseAudioTranscriptDoneEvent).transcript;
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index,
        overwrite: true,
        content: { type: 'text', content: completedText ?? completedTranscript },
        meta: {
          phase: 'done'
        }
      }
    }
    case "error":
      return {
        type: 'error',
        error: {
          code: action.error.code,
          type: action.type,
          message: action.error.message
        }
      }
    case "response.function_call_arguments.done": {
      return {
        id: action.item_id,
        callId: action.call_id,
        type: 'tool_call',
        arguments: action.arguments
      };
    }
    default:
      return {
        type: 'ignore',
        sourceObj: action
      }
  }
};
